import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background button-transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80 hover:shadow-lg hover:glow-primary",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80 hover:shadow-lg",
        outline: "border neon-border bg-transparent text-foreground hover:gradient-bg hover:text-white hover:shadow-lg",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/70 hover:glow-secondary hover:shadow-lg",
        ghost: "hover:bg-accent/50 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline gradient-text hover:opacity-80",
        hero: "gradient-bg text-primary-foreground font-semibold shadow-elevated hover:scale-105 hover:glow-primary hover:shadow-[0_20px_60px_hsl(190_100%_55%/0.4)]",
        neon: "bg-transparent border-2 border-primary text-primary hover:gradient-bg hover:text-primary-foreground hover:glow-primary hover:scale-105",
        "ghost-glow": "bg-transparent text-foreground hover:gradient-bg hover:text-primary-foreground hover:glow-subtle hover:scale-105",
        cta: "gradient-bg-secondary text-secondary-foreground font-bold shadow-card hover:scale-105 hover:shadow-elevated hover:shadow-[0_20px_60px_hsl(265_85%_65%/0.4)]"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-lg px-8 py-3 text-base",
        xl: "h-14 rounded-xl px-10 py-4 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
